import React, { useEffect, useState } from 'react';
import { isEqual } from 'lodash';
import { array, arrayOf, bool, func, objectOf, shape, string } from 'prop-types';
import { Box, Button, FormControl, IconButton, InputLabel, Typography } from '@mui/material';
import { eventNames, reportToSegment, types } from '@smartcar/morse';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';

import { modeOptions, selectFilters, staticText } from '../../../../../../../../localization/Application/VehicleManagement/filtersBar';
import { Form } from '../../../shared/styles';
import { initialState } from '../../../../reducers';
import validateFilterValues from '../../../../utils/validateFilterValues';
import { DateRangePicker, InputField, Select } from '../../../../../../../../components';
import { alphabetize } from '../../../../../../utils';
import AllFiltersDrawer from './components/AllFiltersDrawer';

const FiltersBar = ({
  compatibleVehicles,
  filterValues,
  handleApplyFilters,
  handleResetAll,
  setFormError,
  openSubscribeModal,
  canSubscribeVehicles,
  disableSubscribe,
  openFiltersDrawer,
  setOpenFiltersDrawer,
}) => {
  const [newFormState, setNewFormState] = useState(filterValues);
  const [vehicleIdFocused, setVehicleIdFocused] = useState(false);
  const defaultFilterValues = initialState.filterValues;

  useEffect(() => {
    // reset models whenever make is updated
    setNewFormState({
      ...newFormState,
      model: defaultFilterValues.model,
    });
  }, [newFormState.make]);

  useEffect(() => {
    // because filterValues can be changed elsewhere
    setNewFormState(filterValues);
  }, [filterValues]);

  const getHandleChange = filterKey => (e) => {
    let newValue;
    if (filterKey === 'conDateRange') {
      newValue = e;
    } else {
      newValue = e.target.value;
    }
    setFormError('');
    setNewFormState({
      ...newFormState,
      [filterKey]: newValue,
      ...(filterKey === 'make' && {
        model: defaultFilterValues.model,
      }),
    });
  };

  const getYears = () => {
    const yearRange = [];
    for (let year = 2010; year <= new Date().getFullYear() + 1; year += 1) {
      yearRange.push(year);
    }
    return yearRange;
  };

  const getMenuOptions = (filterKey) => {
    if (filterKey === 'mode') {
      return modeOptions;
    } else if (filterKey === 'make') {
      return Object.keys(compatibleVehicles)
        .map((make) => {
          return {
            value: compatibleVehicles[make].make,
            label: make,
          };
        })
        .sort((a, b) => alphabetize(a.label, b.label));
    } else if (filterKey === 'model') {
      if (newFormState.make === defaultFilterValues.make
        || Object.keys(compatibleVehicles).length === 0) {
        return [];
      }
      return Object.values(compatibleVehicles)
        .find(vehicle => vehicle.make === newFormState.make)
        .models
        .map((model) => {
          return {
            value: model,
            label: model,
          };
        });
    } else if (filterKey === 'year') {
      return getYears().map((year) => {
        return { value: year, label: year };
      });
    }
    return [];
  };

  const getSelectPlaceholder = (filterKey) => {
    if (filterKey === 'make') {
      return 'All makes';
    } else if (filterKey === 'model') {
      return 'All models';
    } else if (filterKey === 'year') {
      return 'All years';
    }
    return null;
  };

  const isDisabled = (filterKey) => {
    if (filterKey === 'model') {
      return newFormState.make === defaultFilterValues.make;
    }
    return false;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleApplyFilters(newFormState);
    reportToSegment(
      types.TRACK,
      eventNames.formSubmitted,
      {
        label: '[vehicles] filter values',
        form_content: newFormState,
      },
    );
  };

  const handleResetClick = () => {
    setFormError('');
    setNewFormState(filterValues);
    handleResetAll();
    reportToSegment(
      types.TRACK,
      eventNames.buttonClicked,
      {
        label: 'reset',
        text: `[vehicles] ${staticText.reset}`,
      },
    );
  };

  const createDateFilter = ({ id, fullWidth }) => (
    <DateRangePicker
      id={id}
      label={staticText.conDate}
      placeholder={staticText.allDates}
      onChange={getHandleChange('conDateRange')}
      value={newFormState.conDateRange}
      fullWidth={fullWidth}
    />
  );

  const createSelectFilters = filters => (
    filters.map(({
      filterKey, id, label, description, minWidth,
    }) => {
      const labelId = `${id}-label`;
      return (
        <FormControl
          disabled={isDisabled(filterKey)}
          key={id}
          variant="outlined"
        >
          <div>
            <InputLabel id={labelId} htmlFor={id} shrink>{label}</InputLabel>
            {description && (
              <Typography variant="caption" component="p" sx={{ marginBottom: 1 }}>
                {description}
              </Typography>
            )}
          </div>
          <Select
            id={id}
            name={filterKey}
            labelId={labelId}
            options={getMenuOptions(filterKey)}
            value={newFormState[filterKey]}
            onChange={getHandleChange(filterKey)}
            placeholderOptionLabel={getSelectPlaceholder(filterKey)}
            disabled={isDisabled(filterKey)}
            selectStyles={{ width: minWidth || '100%' }}
          />
        </FormControl>
      );
    })
  );

  const createVehicleIdFilter = ({ id, width, showErrorMessage = false }) => {
    const errorMessage = !vehicleIdFocused
      && validateFilterValues({ vehicleId: newFormState.vehicleId }).error;
    return (
      <InputField
        inputName={id}
        label={staticText.vehicleId}
        inputPlaceholder={staticText.enterVehicleId}
        handleChange={getHandleChange('vehicleId')}
        inputValues={newFormState.vehicleId}
        customInputWidth={width}
        fullWidth={!width}
        handleBlur={() => setVehicleIdFocused(false)}
        handleFocus={() => setVehicleIdFocused(true)}
        {...showErrorMessage ? {
          errorMessage,
        } : {
          error: !!errorMessage,
        }}
      />
    );
  };

  const submitButton = (
    <Button
      type="submit"
      variant="outlined"
      disabled={isEqual(newFormState, filterValues)}
      size="small"
    >
      {staticText.apply}
    </Button>
  );
  const createResetButton = (overrides = {}) => (
    <Button
      id="reset-all-button"
      onClick={handleResetClick}
      variant="text"
      size="small"
      {...overrides}
    >
      {staticText.reset}
    </Button>
  );

  return (
    <Form onSubmit={handleSubmit}>
      <Box display="flex" alignItems="flex-end" gap={1} flexWrap="wrap">
        {createDateFilter({ id: 'date-range-picker' })}
        {createSelectFilters(selectFilters)}
        {createVehicleIdFilter({ id: 'vehicle-id', width: '300px' })}
        <IconButton onClick={() => setOpenFiltersDrawer(!openFiltersDrawer)}>
          <TuneRoundedIcon color="primary" />
        </IconButton>
        {openFiltersDrawer && (
          <AllFiltersDrawer
            closeDrawer={() => setOpenFiltersDrawer(false)}
            createDateFilter={createDateFilter}
            submitButton={submitButton}
            resetButton={createResetButton({ variant: 'outlined', color: 'secondary' })}
            handleSubmit={handleSubmit}
            createSelectFilters={createSelectFilters}
            createVehicleIdFilter={createVehicleIdFilter}
          />
        )}
        {submitButton}
        {createResetButton()}
      </Box>
      {canSubscribeVehicles &&
        <Button variant="outlined" color="secondary" size="small" onClick={openSubscribeModal} disabled={disableSubscribe} sx={{ marginLeft: 'auto' }}>
          {staticText.subscribe}
        </Button>
      }
    </Form>
  );
};

export default FiltersBar;

FiltersBar.propTypes = {
  compatibleVehicles: objectOf(
    shape({
      make: string.isRequired,
      models: arrayOf(string).isRequired,
    }),
  ).isRequired,
  filterValues: shape({
    make: string.isRequired,
    model: string.isRequired,
    vehicleId: string.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    conDateRange: array.isRequired,
  }).isRequired,
  handleApplyFilters: func.isRequired,
  handleResetAll: func.isRequired,
  setFormError: func.isRequired,
  openSubscribeModal: func.isRequired,
  canSubscribeVehicles: bool.isRequired,
  disableSubscribe: bool.isRequired,
  openFiltersDrawer: bool.isRequired,
  setOpenFiltersDrawer: func.isRequired,
};
